import React, { createContext, useContext } from "react";
import {
  Toaster,
  ToastId,
  ToasterProps,
  useToastController,
  FluentProvider,
  ToastTitle,
  ToastBody,
  Toast,
  webLightTheme,
  ToastTrigger,
  ToastFooter,
  ToastIntent,
} from "@fluentui/react-components";
import { IconButton, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { useNavigateWithParams } from "utils/components/LinkWithParams";
import { getCollectionDetailUrl } from "pages/Catalog2/helpers";

interface ToasterContextValue {
  addToast: (toast: ToasterProps) => ToastId;
}

const ToasterContext = createContext<ToasterContextValue | undefined>(undefined);

export const useToaster = (): ToasterContextValue => {
  const ctx = useContext(ToasterContext);
  if (!ctx) {
    throw new Error("useToaster must be used within a ToasterProvider");
  }
  return ctx;
};

export const useGlobalToaster = () => {
  // Retrieve toast controller from context
  const { dispatchToast, dismissToast } = useToastController("global-toaster");
  const navigate = useNavigateWithParams();

  // Add a toast notification
  const addToast = (
    message: string,
    title: string,
    actionId?: string,
    intent?: ToastIntent,
    subtitle?: string,
    toastId?: ToastId
  ) => {
    dispatchToast(
      <Toast>
        {intent === "info" ? (
          <ToastTitle
            media={<Spinner size={SpinnerSize.small} />}
            action={
              <ToastTrigger>
                <IconButton iconProps={{ iconName: "Cancel" }} />
              </ToastTrigger>
            }
          >
            {title}
          </ToastTitle>
        ) : (
          <ToastTitle
            action={
              <ToastTrigger>
                <IconButton iconProps={{ iconName: "Cancel" }} />
              </ToastTrigger>
            }
          >
            {title}
          </ToastTitle>
        )}
        <ToastBody subtitle={subtitle}>{message}</ToastBody>
        {actionId && (
          <ToastFooter>
            <PrimaryButton
              onClick={() => {
                console.log(
                  "Navigating to collection",
                  getCollectionDetailUrl(actionId)
                );
                actionId && navigate(getCollectionDetailUrl(actionId));
              }}
              ariaLabel="Go to Collection"
            >
              Go to Collection
            </PrimaryButton>
          </ToastFooter>
        )}
      </Toast>,
      /* Don't dismiss the info toast automatically */
      intent === "info"
        ? {
            intent: intent || "success",
            position: "top-end",
            toastId: toastId || undefined,
            timeout: -1,
          }
        : {
            intent: intent || "success",
            position: "top-end",
            toastId: toastId || undefined,
            timeout: 10000,
          }
    );
  };

  const removeToast = (toastId: ToastId) => {
    dismissToast(toastId);
  };
  return { addToast, removeToast };
};

export const ToasterProvider: React.FC = ({ children }) => {
  return (
    <FluentProvider theme={webLightTheme}>
      {children}
      <Toaster toasterId="global-toaster" />
    </FluentProvider>
  );
};
